import React, { useState, useMemo } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { QK } from '../../lib/queryKeys';
import { fetchInvoice as fetchInvoiceFn, fetchInvoiceStandardItems, fetchHorses } from '../../lib/queryFunctions';
import SidebarLayout from '../../components/layout/SidebarLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import EmailSendHistory from '../../components/shared/EmailSendHistory';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Textarea } from '../../components/ui/textarea';
import { Skeleton } from '../../components/ui/skeleton';
import { ArrowLeft, Download, Mail, CheckCircle2, DollarSign, Upload, Trash2, Plus, Pencil, Calendar, Copy, Paperclip, Undo2, Globe, FileText } from 'lucide-react';
import { toast } from 'sonner';
import api from '../../services/api';
import InvoiceEmailDialog from '../../components/invoices/InvoiceEmailDialog';

import { calcInvoiceAmount } from '../../utils/invoiceCalc';
import { Checkbox } from '../../components/ui/checkbox';

const GREEN_DOOR_LOGO = 'https://customer-assets.emergentagent.com/job_thoroughbred-deals/artifacts/xmrjz10b_Powered%20By%20Green%20Door.png';

const fmt = (amount) => {
  if (!amount && amount !== 0) return '-';
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(amount);
};

const InvoiceDetailPage = () => {
  const navigate = useNavigate();
  const { invoiceId } = useParams();
  const qc = useQueryClient();

  // ── Server state — staleTime:0 so draft live-read values are always current ──
  const INVOICE_OPTS = { staleTime: 0, refetchOnMount: 'always' };

  const { data: invoiceData, isLoading: loading } = useQuery({
    queryKey: QK.invoice(invoiceId),
    queryFn: async () => {
      // Phase 1: fetch invoice + supporting data in parallel
      const [invRes, itemsRes, oppsRes, horsesRes] = await Promise.all([
        api.get(`/invoices/${invoiceId}`),
        api.get('/invoices/standard-items'),
        api.get('/sale-opportunities'),
        api.get('/horses'),
      ]);
      const inv = invRes.data;
      const allOpps = (oppsRes.data || []).filter(o => !o.archived);
      const filteredOpps = inv?.horse_id ? allOpps.filter(o => o.horse_id === inv.horse_id) : allOpps;
      // Phase 2: enrich opps with sale name in parallel
      const enriched = await Promise.all(filteredOpps.map(async (opp) => {
        try {
          const saleRes = await api.get(`/sales/${opp.sale_id}`);
          const sale = saleRes.data;
          return { ...opp, sale_name: sale.sale_name, sale_company: sale.sale_company, sale_year: sale.sale_start_date?.substring(0, 4) || '' };
        } catch { return opp; }
      }));
      return { invoice: inv, standardItems: itemsRes.data || [], horses: horsesRes.data || [], saleOpportunities: enriched };
    },
    enabled: !!invoiceId,
    onError: () => toast.error('Failed to load invoice'),
    ...INVOICE_OPTS,
  });

  const invoice          = invoiceData?.invoice ?? null;
  const standardItems    = invoiceData?.standardItems ?? [];
  const horses           = invoiceData?.horses ?? [];
  const saleOpportunities = invoiceData?.saleOpportunities ?? [];

  /** Invalidates the invoice AND the invoice list so amounts stay in sync everywhere. */
  const invalidateInvoice = () => {
    qc.invalidateQueries({ queryKey: QK.invoice(invoiceId) });
    qc.invalidateQueries({ queryKey: QK.accountInvoices() });
    qc.invalidateQueries({ queryKey: QK.invoiceDashboard() });
    // If horse is linked, also refresh the horse profile's invoice section
    if (invoice?.horse_id) {
      qc.invalidateQueries({ queryKey: QK.horse(invoice.horse_id) });
    }
  };

  // ── UI state ──────────────────────────────────────────────────────────────
  const [addItemOpen, setAddItemOpen] = useState(false);
  const [addSaleOpen, setAddSaleOpen] = useState(false);
  const [addHorseOpen, setAddHorseOpen] = useState(false);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const [commissionSplitDialog, setCommissionSplitDialog] = useState(null);
  const [showDueDatePicker, setShowDueDatePicker] = useState(false);
  const [showInvDatePicker, setShowInvDatePicker] = useState(false);
  const [showInvNumEdit, setShowInvNumEdit] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(false);
  const [showBillingPeriodEdit, setShowBillingPeriodEdit] = useState(false);
  const [publishDialogOpen, setPublishDialogOpen] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null);

  const handleApprove = async () => {
    setConfirmAction({
      message: 'Once approved, changes can no longer be made to this invoice. Are you sure you want to approve?',
      onConfirm: async () => {
        try { await api.post(`/invoices/${invoiceId}/approve`); toast.success('Invoice approved'); invalidateInvoice(); } catch { toast.error('Failed to approve'); }
        setConfirmAction(null);
      }
    });
  };
  const handleDeliver = async (method) => { try { await api.post(`/invoices/${invoiceId}/deliver`, { method }); toast.success('Invoice delivered'); invalidateInvoice(); } catch { toast.error('Failed'); } };
  const handleUndoManualDelivery = async () => { try { await api.post(`/invoices/${invoiceId}/undo-manual-delivery`); toast.success('Manual delivery undone'); invalidateInvoice(); } catch { toast.error('Failed'); } };
  const [payNotesOpen, setPayNotesOpen] = useState(false);
  const [payNotes, setPayNotes] = useState('');
  const [payNotesViewOpen, setPayNotesViewOpen] = useState(false);
  const [payNotesEditing, setPayNotesEditing] = useState(false);
  const [payNotesEdit, setPayNotesEdit] = useState('');
  const [notesTermsEditing, setNotesTermsEditing] = useState(false);
  const [notesTermsEdit, setNotesTermsEdit] = useState('');
  const [savingNotesTerms, setSavingNotesTerms] = useState(false);
  const handlePay = async () => {
    try {
      await api.post(`/invoices/${invoiceId}/pay`, { notes: payNotes });
      toast.success('Marked as paid');
      setPayNotesOpen(false);
      setPayNotes('');
      invalidateInvoice();
    } catch { toast.error('Failed'); }
  };
  const handleUnpay = async () => { try { await api.post(`/invoices/${invoiceId}/unpay`); toast.success('Payment status reverted to unpaid'); invalidateInvoice(); } catch { toast.error('Failed'); } };
  const handleUnpublish = async () => { try { await api.post(`/invoices/${invoiceId}/unpublish-from-portal`); toast.success('Invoice removed from portal'); invalidateInvoice(); } catch { toast.error('Failed'); } };

  const handleSaveNotesTerms = async () => {
    setSavingNotesTerms(true);
    try {
      await api.put(`/invoices/${invoiceId}`, { other_notes_terms: notesTermsEdit.trim() || null });
      toast.success('Notes / Additional Terms saved');
      setNotesTermsEditing(false);
      invalidateInvoice();
    } catch { toast.error('Failed to save'); }
    finally { setSavingNotesTerms(false); }
  };
  const handleDuplicate = async () => {
    try {
      const res = await api.post(`/invoices/${invoiceId}/duplicate-for-owners`);
      const data = res.data;
      if (data.created === 0) { toast.info('No other owners found'); return; }
      toast.success(`Created ${data.created} invoice(s) for other owners: ${data.invoices.map(i => `${i.crm_entity_name} (${i.owner_percent}%)`).join(', ')}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to duplicate');
    }
  };

  const handleDuplicateForSyndicateMembers = async () => {
    try {
      const res = await api.post(`/invoices/${invoiceId}/duplicate-for-syndicate-members`);
      const data = res.data;
      if (data.created === 0) { toast.info('No other syndicate members found'); return; }
      toast.success(`Created ${data.created} invoice(s) for other syndicate members: ${data.invoices.map(i => `${i.crm_entity_name} (${i.owner_percent}%)`).join(', ')}`);
      invalidateInvoice();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to duplicate for syndicate members');
    }
  };
  const handleDelete = async () => {
    setConfirmAction({
      message: 'Delete this invoice?',
      onConfirm: async () => {
        try { await api.delete(`/invoices/${invoiceId}`); toast.success('Invoice deleted'); navigate('/invoices'); } catch { toast.error('Failed to delete'); }
        setConfirmAction(null);
      }
    });
  };

  const handleAddSale = async (oppId) => {
    try {
      const saleData = await api.get(`/invoices/generate-from-sale/${oppId}`);
      const data = saleData.data;
      const saleEntries = (data.entries || []).map(e => ({
        id: crypto.randomUUID(), invoice_id: invoiceId, items: e.items, description: e.description || '',
        units: 1, price: parseFloat(e.price) || 0, total: parseFloat(e.price) || 0,
        currency: invoice.currency || 'USD', date: new Date().toISOString(), sale_opportunity_id: oppId,
      }));
      const allEntries = [...(invoice.entries || []), ...saleEntries];

      // Get commission info
      let commissionAmount = 0;
      let commissionRate = 0;
      let commissionSplit = false;
      let commissionSplitPercent = 0;
      let splitEntityName = '';
      let splitEntityId = '';
      let minCommission = 0;  // minimum commission floor from CRM entity
      if (invoice.crm_entity_id) {
        try {
          const entityRes = await api.get(`/crm-entities/${invoice.crm_entity_id}`);
          commissionRate = parseFloat(entityRes.data?.commission_rate_percent || 0);
          commissionSplit = entityRes.data?.commission_split || false;
          commissionSplitPercent = parseFloat(entityRes.data?.commission_split_percent_linked || 0);
          splitEntityName = entityRes.data?.commission_split_entity_name || '';
          splitEntityId = entityRes.data?.commission_split_with_entity_id || '';
          minCommission = parseFloat(entityRes.data?.minimum_commission_amount || 0);
          if (commissionSplit && splitEntityId) {
            try { const splitRes = await api.get(`/crm-entities/${splitEntityId}`); splitEntityName = splitRes.data?.system_name || splitEntityName; } catch { /* ignore */ }
          }
        } catch { /* ignore */ }
      }

      if (commissionSplit && commissionSplitPercent > 0 && data.net_proceeds > 0 && commissionRate > 0) {
        // Ask user about commission split
        setCommissionSplitDialog({
          oppId, allEntries, data, commissionRate, commissionSplitPercent, splitEntityName, splitEntityId,
          saleName: data.sale_name || '',
        });
        setAddSaleOpen(false);
        return;
      }

      // No split - apply full commission
      if (data.net_proceeds > 0 && commissionRate > 0) {
        commissionAmount = data.net_proceeds * (commissionRate / 100);
      }
      // Apply minimum commission floor (covers both sold where calculated < minimum, and RNA where net_proceeds = 0)
      if (minCommission > 0 && commissionAmount < minCommission) {
        commissionAmount = minCommission;
      }

      await api.put(`/invoices/${invoiceId}`, {
        entries: allEntries,
        sale_opportunity_id: oppId,
        add_sale_proceeds: true,
        sale_net_proceeds: data.net_proceeds || 0,
        commission_rate: commissionRate,
        commission_amount: commissionAmount,
        commission_split_applied: false,
        commission_split_percent: 0,
        horse_id: invoice.horse_id || data.horse_id,
      });
      toast.success('Sale details added');
      setAddSaleOpen(false);
      invalidateInvoice();
    } catch { toast.error('Failed to add sale'); }
  };

  const handleCommissionSplitChoice = async (myPortionOnly) => {
    const { oppId, allEntries, data, commissionRate, commissionSplitPercent, splitEntityName, splitEntityId, saleName } = commissionSplitDialog;
    const fullCommission = data.net_proceeds * (commissionRate / 100);

    if (myPortionOnly) {
      const myPortion = fullCommission * (1 - commissionSplitPercent / 100);
      await api.put(`/invoices/${invoiceId}`, {
        entries: allEntries,
        sale_opportunity_id: oppId,
        add_sale_proceeds: true,
        sale_net_proceeds: data.net_proceeds || 0,
        commission_rate: commissionRate,
        commission_amount: myPortion,
        commission_split_applied: true,
        commission_split_percent: commissionSplitPercent,
        horse_id: invoice.horse_id || data.horse_id,
      });
      toast.success(`Commission reflects your portion (${(100 - commissionSplitPercent)}% of ${commissionRate}%)`);
    } else {
      // Full commission on this invoice
      await api.put(`/invoices/${invoiceId}`, {
        entries: allEntries,
        sale_opportunity_id: oppId,
        add_sale_proceeds: true,
        sale_net_proceeds: data.net_proceeds || 0,
        commission_rate: commissionRate,
        commission_amount: fullCommission,
        commission_split_applied: false,
        commission_split_percent: 0,
        horse_id: invoice.horse_id || data.horse_id,
      });
      // Create separate invoice for commission split
      try {
        await api.post('/invoices', {
          crm_entity_id: splitEntityId || invoice.crm_entity_id,
          horse_id: invoice.horse_id || data.horse_id,
          sale_opportunity_id: oppId,
          entries: [{ items: 'Commission Split Payment', description: `${saleName ? saleName + ' - ' : ''}${commissionSplitPercent}% of ${commissionRate}% commission to ${splitEntityName}`, units: 1, price: fullCommission * (commissionSplitPercent / 100) }],
          add_sale_proceeds: false,
          is_commission_split_invoice: true,
          other_notes_terms: `Commission split payment: ${saleName ? saleName + ' - ' : ''}${commissionSplitPercent}% of ${commissionRate}% commission`,
        });
        toast.success('Full commission applied. Separate invoice created for commission split payment.');
      } catch {
        toast.success('Full commission applied. Failed to create split invoice.');
      }
    }
    setCommissionSplitDialog(null);
    invalidateInvoice();
  };

  const addLineItem = async (item) => {
    let price = parseFloat(item.price) || 0;
    const isRebillable = item.is_rebillable || false;
    const isProceeds   = item.is_proceeds   || false;
    let markup = 0;
    // Apply expense markup for rebillable items (never on proceeds entries)
    if (isRebillable && !isProceeds && invoice.crm_entity_id) {
      try {
        const entityRes = await api.get(`/crm-entities/${invoice.crm_entity_id}`);
        markup = parseFloat(entityRes.data?.expense_markup_percent || 0);
        if (markup > 0) price = price * (1 + markup / 100);
      } catch { /* ignore */ }
    }
    const units = parseFloat(item.units) || 1;
    const newEntry = {
      id: crypto.randomUUID(),
      invoice_id: invoiceId,
      items: item.items,
      description: (item.description || '') + (isRebillable ? ' (Rebillable)' : ''),
      units,
      price,
      total: price * units,
      currency: invoice.currency || 'USD',
      date: new Date().toISOString(),
      is_rebillable: isRebillable,
      is_proceeds: isProceeds,
      markup_percent: markup,
      is_direct_allocation: !!(item.is_direct_allocation),
    };
    const entries = [...(invoice.entries || []), newEntry];
    await saveEntries(entries);
    setAddItemOpen(false);
  };

  const saveEntries = async (entries) => {
    // Expense-only subtotal (excludes proceeds) — stored as total_expenses for accounting
    const expenseEntries = entries.filter(e => !e.sale_opportunity_id && !e.is_proceeds);
    const totalExpenses  = expenseEntries.reduce((s, e) => s + (e.total || e.price * e.units), 0);
    const discountAmt    = invoice.add_discount ? (invoice.discount_unit === 'percent' ? totalExpenses * ((invoice.discount_amount || 0) / 100) : (invoice.discount_amount || 0)) : 0;
    const expenseNet     = totalExpenses - discountAmt;
    const proceedsEntries  = entries.filter(e => !e.sale_opportunity_id && !!e.is_proceeds);
    const proceedsSubtotal = proceedsEntries.reduce((s, e) => s + (e.total || e.price * e.units), 0);

    // invoice_total = net of the expenses section (other proceeds - expenses).
    // Sale proceeds are handled separately by the backend from the linked opportunity.
    const expSectionNet = proceedsSubtotal > 0 ? proceedsSubtotal - expenseNet : expenseNet;
    const invoiceTotal  = expSectionNet;
    const ownerTotal    = (invoice.owner_percent / 100) * Math.abs(invoiceTotal);
    try {
      await api.put(`/invoices/${invoiceId}`, {
        entries,
        total_expenses: totalExpenses,
        invoice_total: invoiceTotal,
        owner_portion_total: ownerTotal,
        discount_total: discountAmt,
      });
      invalidateInvoice();
    } catch { toast.error('Failed to update'); }
  };

  const deleteEntry = async (idx) => {
    setConfirmAction({
      message: 'Remove this line item?',
      onConfirm: async () => {
        const entries = [...(invoice.entries || [])];
        entries.splice(idx, 1);
        await saveEntries(entries);
        setConfirmAction(null);
      }
    });
  };

  const [editEntryIdx, setEditEntryIdx] = useState(null);
  const [editEntryForm, setEditEntryForm] = useState({ items: '', description: '', units: 1, price: 0, base_cost: 0, markup_percent: 0, is_rebillable: false, is_direct_allocation: false });

  const startEditEntry = (idx) => {
    const e = invoice.entries[idx];
    const isRebillable = e.is_rebillable || false;
    const markup = isRebillable ? (e.markup_percent || 0) : 0;
    const baseCost = isRebillable && markup > 0 ? e.price / (1 + markup / 100) : e.price;
    setEditEntryForm({ items: e.items, description: e.description || '', units: e.units, price: e.price, base_cost: Math.round(baseCost * 100) / 100, markup_percent: markup, is_rebillable: isRebillable, is_direct_allocation: !!(e.is_direct_allocation) });
    setEditEntryIdx(idx);
  };

  const saveEditEntry = async () => {
    const entries = [...(invoice.entries || [])];
    const finalPrice = editEntryForm.is_rebillable && editEntryForm.markup_percent > 0
      ? editEntryForm.base_cost * (1 + editEntryForm.markup_percent / 100)
      : editEntryForm.price;
    entries[editEntryIdx] = { ...entries[editEntryIdx], items: editEntryForm.items, description: editEntryForm.description, units: editEntryForm.units, price: Math.round(finalPrice * 100) / 100, total: Math.round(finalPrice * editEntryForm.units * 100) / 100, is_rebillable: editEntryForm.is_rebillable, markup_percent: editEntryForm.markup_percent, is_direct_allocation: !!(editEntryForm.is_direct_allocation) };
    await saveEntries(entries);
    setEditEntryIdx(null);
    toast.success('Line item updated');
  };

  if (loading) return <SidebarLayout><div className="space-y-6"><Skeleton className="h-10 w-64" /><Skeleton className="h-96 w-full" /></div></SidebarLayout>;
  if (!invoice) return <SidebarLayout><div className="text-center py-12"><p className="text-muted-foreground">Invoice not found</p><Button variant="outline" className="mt-4" onClick={() => navigate('/invoices')}>Go Back</Button></div></SidebarLayout>;

  const isDraft = invoice.invoice_status === 'draft';
  const isApproved = invoice.invoice_status === 'approved';
  // Treat as "delivered" if status starts with 'delivered', OR if the invoice
  // has been published/emailed (those actions don't change invoice_status)
  const isDelivered = invoice.invoice_status?.startsWith('delivered')
    || !!invoice.published_to_portal
    || (invoice.email_sends && invoice.email_sends.length > 0);
  const entries = invoice.entries || [];
  const {
    expenseSubtotal,
    proceedsSubtotal,
    discountTotal,
    expenseTotal,
    hasOtherProceeds,
    saleProceeds: fullSaleProceeds,
    hasSaleDetails,
    grandTotal,
    amount: amountDue,
    isPayable: is_payable_invoice,
    // Direct Allocation fields
    hasDirectAllocations,
    sharedExpenseSubtotal,
    sharedProceedsSubtotal,
    directSubtotal,
    directExpenseSubtotal,
    directProceedsSubtotal,
    discountOnShared,
    discountOnDirect,
    sharedGrandTotal,
  } = calcInvoiceAmount(invoice);
  const saleNetProceeds    = invoice.sale_net_proceeds || 0;
  const saleFees           = (invoice.sale_company_chargeback || 0) + (invoice.sale_company_entry_fee || 0) + (invoice.sale_company_commission || 0) + (invoice.sale_company_lien || 0) + (invoice.sale_company_other_amount || 0);
  const ownerSaleProceeds  = invoice.owner_portion_sale_net_proceeds || 0;
  const amountLabel        = is_payable_invoice ? 'AMOUNT PAYABLE' : 'AMOUNT DUE';
  const amountLabelShort   = is_payable_invoice ? 'Amount Payable' : 'Amount Due';

  return (
    <SidebarLayout>
      <div className="space-y-4">
        {/* Top Bar */}
        <div className="flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate('/invoices')}><ArrowLeft className="w-4 h-4 mr-2" /> Back to Invoices</Button>
          <div className="flex items-center gap-2">
            <Badge className={isDraft ? 'bg-gray-100 text-gray-700' : isApproved ? 'bg-blue-100 text-blue-700' : 'bg-green-100 text-green-700'}>{invoice.invoice_status?.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}</Badge>
            <Badge className={invoice.payment_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}>{invoice.payment_status?.replace(/\b\w/g, c => c.toUpperCase())}</Badge>
          </div>
        </div>

        {/* Actions */}
        <Card>
          <CardContent className="p-3 flex flex-wrap gap-2 items-center">
            {isDraft && <Button size="sm" onClick={handleApprove} className="bg-primary hover:bg-primary/90 text-white"><CheckCircle2 className="w-4 h-4 mr-2" /> Approve Invoice</Button>}
            {(isApproved || isDelivered) && (
              <div className="flex flex-wrap gap-2 items-center w-full">
                {/* Download — always visible */}
                <Button size="sm" variant="outline" onClick={async () => {
                  try {
                    const res = await api.get(`/invoices/${invoiceId}/pdf`, { responseType: 'blob' });
                    const blob = new Blob([res.data], { type: 'application/pdf' });
                    const url = window.URL.createObjectURL(blob);
                    const link = document.createElement('a');
                    link.href = url;
                    link.download = `${invoice.invoice_number_formatted || 'invoice'}.pdf`;
                    document.body.appendChild(link); link.click(); document.body.removeChild(link);
                    setTimeout(() => window.URL.revokeObjectURL(url), 100);
                  } catch { toast.error('Failed to generate PDF'); }
                }}><Download className="w-4 h-4 mr-2" /> Download PDF</Button>

                {/* Email — only shown when no email has been sent yet */}
                {(!invoice.email_sends || invoice.email_sends.length === 0) && (
                  <Button size="sm" variant="outline" onClick={() => setEmailDialogOpen(true)}><Mail className="w-4 h-4 mr-2" /> Email via Green Door</Button>
                )}

                {/* Publish — visible until published, then disabled */}
                <Button size="sm" variant="outline" onClick={() => setPublishDialogOpen(true)} disabled={invoice.published_to_portal}>
                  <Globe className="w-4 h-4 mr-2" /> {invoice.published_to_portal ? 'Published' : 'Publish to Portal'}
                </Button>

                {/* Mark as Delivered — only shown when not yet delivered */}
                {!isDelivered && (
                  <Button size="sm" variant="outline" onClick={() => handleDeliver('manually')}>Mark as Delivered</Button>
                )}

                {/* Mark as Paid — shown when delivered and not yet paid */}
                {isDelivered && invoice.payment_status !== 'paid' && (
                  <Button size="sm" onClick={() => { setPayNotes(''); setPayNotesOpen(true); }} className="bg-primary hover:bg-primary/90 text-white"><DollarSign className="w-4 h-4 mr-2" /> Mark as Paid</Button>
                )}

                {/* Duplicate for other horse owners */}
                {isApproved && !isDelivered && invoice.has_other_owners && (
                  <Button size="sm" variant="outline" onClick={handleDuplicate}><Copy className="w-4 h-4 mr-2" /> Duplicate for Other Owners</Button>
                )}

                {/* Duplicate for other syndicate members */}
                {isApproved && !isDelivered && invoice.syndicate_id && (
                  invoice.syndicate_duplication_at ? (
                    <div className="flex items-start gap-2 border rounded-md px-3 py-1.5 bg-green-50 border-green-200">
                      <CheckCircle2 className="w-3.5 h-3.5 text-green-600 shrink-0 mt-0.5" />
                      <div className="min-w-0">
                        <p className="text-xs text-green-700 font-medium">
                          Duplicated {new Date(invoice.syndicate_duplication_at).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}
                          {invoice.syndicate_duplication_by_name ? ` by ${invoice.syndicate_duplication_by_name}` : ''}
                        </p>
                        {(invoice.syndicate_duplication_invoices || []).length > 0 && (
                          <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-0.5">
                            {invoice.syndicate_duplication_invoices.map(dup => (
                              <Link
                                key={dup.id}
                                to={`/invoices/${dup.id}`}
                                className="text-xs text-primary hover:underline font-medium"
                              >
                                #{dup.invoice_number_formatted} — {dup.crm_entity_name} ({dup.owner_percent}%)
                              </Link>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <Button size="sm" variant="outline" onClick={handleDuplicateForSyndicateMembers} data-testid="duplicate-syndicate-members-btn">
                      <Copy className="w-4 h-4 mr-2" /> Duplicate for Other Syndicate Members
                    </Button>
                  )
                )}

                <div className="ml-auto">
                  <Button variant="ghost" size="sm" className="text-muted-foreground hover:text-red-600" onClick={handleDelete}><Trash2 className="w-4 h-4" /></Button>
                </div>
              </div>
            )}
            {isDelivered && (
              <div className="flex flex-col gap-2 w-full">
                {/* Email send history — only real sends */}
                {invoice.email_sends && invoice.email_sends.length > 0 && (
                  <EmailSendHistory sends={invoice.email_sends} onResend={() => setEmailDialogOpen(true)} emlFilename="invoice_email.eml" />
                )}
                {/* Manually delivered details */}
                {invoice.invoice_status === 'delivered_manually' && invoice.delivery_date && (
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2 border rounded-md px-3 py-1.5 bg-amber-50 border-amber-200">
                      <CheckCircle2 className="w-3.5 h-3.5 text-amber-600 flex-shrink-0" />
                      <span className="text-xs text-amber-700">
                        Marked as delivered on {new Date(invoice.delivery_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' })}{invoice.delivery_sent_by ? ` by ${invoice.delivery_sent_by}` : ''}
                      </span>
                    </div>
                    <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-red-600 text-xs h-7" onClick={handleUndoManualDelivery}><Undo2 className="w-3.5 h-3.5 mr-1" /> Undo</Button>
                  </div>
                )}
                {/* Publish details row */}
                {invoice.published_to_portal && (
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-2 border rounded-md px-3 py-1.5 bg-blue-50 border-blue-200">
                      <Globe className="w-3.5 h-3.5 text-blue-600 flex-shrink-0" />
                      <span className="text-xs text-blue-700">
                        Published to Portal on {invoice.published_to_portal_at ? new Date(invoice.published_to_portal_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}{invoice.published_to_portal_by ? ` by ${invoice.published_to_portal_by}` : ''}
                      </span>
                    </div>
                    <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-red-600 text-xs h-7" onClick={handleUnpublish}><Undo2 className="w-3.5 h-3.5 mr-1" /> Undo Publish</Button>
                  </div>
                )}
                {/* Payment details row */}
                {invoice.payment_status === 'paid' && (
                  <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex items-center gap-2 border rounded-md px-3 py-1.5 bg-emerald-50 border-emerald-200">
                      <DollarSign className="w-3.5 h-3.5 text-emerald-600 flex-shrink-0" />
                      <span className="text-xs text-emerald-700">
                        Paid on {invoice.paid_at ? new Date(invoice.paid_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}{invoice.paid_by ? ` by ${invoice.paid_by}` : ''}
                      </span>
                    </div>
                    {invoice.payment_notes && (
                      <Button size="sm" variant="outline" className="text-xs h-7 gap-1" onClick={() => setPayNotesViewOpen(true)}>
                        <FileText className="w-3 h-3" /> View Payment Notes
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="text-muted-foreground hover:text-red-600 text-xs h-7" onClick={handleUnpay}><Undo2 className="w-3.5 h-3.5 mr-1" /> Undo Payment</Button>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Invoice Document */}
        <Card>
          <CardContent className="p-8 space-y-8">
            {/* 1. TENANT HEADER */}
            <div className="flex justify-between items-start">
              <div>
                {invoice.tenant_logo_url && <img src={invoice.tenant_logo_url} alt="Logo" className="h-16 mb-2 object-contain" />}
                {!invoice.tenant_logo_url && <p className="font-serif text-2xl font-bold">{invoice.tenant_name}</p>}
              </div>
              <div className="text-right">
                <p className="font-serif text-3xl font-bold text-muted-foreground mb-2">INVOICE</p>
                <p className="text-sm font-medium">{invoice.tenant_name}</p>
                {invoice.tenant_address && <p className="text-xs text-muted-foreground">{invoice.tenant_address}</p>}
                {invoice.tenant_email && <p className="text-xs text-muted-foreground">{invoice.tenant_email}</p>}
                {invoice.tenant_phone && <p className="text-xs text-muted-foreground">{invoice.tenant_phone}</p>}
              </div>
            </div>

            {/* 2. INVOICE DETAILS */}
            <div className="flex justify-between border-t border-b py-4">
              <div>
                <p className="text-xs text-muted-foreground font-semibold mb-1">BILL TO</p>
                <p className="text-sm font-semibold">{invoice.crm_entity_name || '-'}</p>
                {invoice.crm_entity_address && <p className="text-xs text-muted-foreground">{invoice.crm_entity_address}</p>}
                {(invoice.crm_entity_city || invoice.crm_entity_state || invoice.crm_entity_zip) && (
                  <p className="text-xs text-muted-foreground">{[invoice.crm_entity_city, invoice.crm_entity_state, invoice.crm_entity_zip].filter(Boolean).join(', ')}</p>
                )}
                {invoice.crm_entity_email && <p className="text-xs text-muted-foreground">{invoice.crm_entity_email}</p>}
              </div>
              <div className="text-right text-sm space-y-1">
                <div>
                  <div className="flex items-center gap-1 justify-end">
                    <span className="text-muted-foreground">Invoice #: </span>
                    <span className="font-medium">{invoice.invoice_number_formatted}</span>
                    {isDraft && (
                      <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-muted-foreground hover:text-primary" onClick={() => setShowInvNumEdit(!showInvNumEdit)}>
                        <Pencil className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                  {showInvNumEdit && (
                    <div className="mt-1 flex justify-end">
                      <Input className="h-7 w-40 text-xs" defaultValue={invoice.invoice_number_formatted || ''} onKeyDown={async (e) => {
                        if (e.key === 'Enter') {
                          try { await api.put(`/invoices/${invoiceId}`, { invoice_number_formatted: e.target.value }); setShowInvNumEdit(false); invalidateInvoice(); } catch { /* ignore */ }
                        }
                      }} onBlur={async (e) => {
                        if (e.target.value !== invoice.invoice_number_formatted) {
                          try { await api.put(`/invoices/${invoiceId}`, { invoice_number_formatted: e.target.value }); setShowInvNumEdit(false); invalidateInvoice(); } catch { /* ignore */ }
                        } else { setShowInvNumEdit(false); }
                      }} autoFocus />
                    </div>
                  )}
                </div>
                {(invoice.period_start_date || invoice.period_end_date || isDraft) && (
                  <div>
                    <div className="flex items-center gap-1 justify-end">
                      <span className="text-muted-foreground">Billing Period: </span>
                      <span className="font-medium">
                        {(invoice.period_start_date || invoice.period_end_date) 
                          ? `${invoice.period_start_date ? new Date(invoice.period_start_date).toLocaleDateString() : '-'} - ${invoice.period_end_date ? new Date(invoice.period_end_date).toLocaleDateString() : '-'}`
                          : 'Not set'}
                      </span>
                      {isDraft && (
                        <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-muted-foreground hover:text-primary" onClick={() => setShowBillingPeriodEdit(!showBillingPeriodEdit)}>
                          <Pencil className="w-3 h-3" />
                        </Button>
                      )}
                    </div>
                    {showBillingPeriodEdit && (
                      <div className="mt-1 flex justify-end gap-2">
                        <Input type="date" className="h-7 w-36 text-xs date-filled" defaultValue={invoice.period_start_date ? new Date(invoice.period_start_date).toISOString().split('T')[0] : ''} onChange={async (e) => {
                          if (e.target.value) {
                            const [y, m, d] = e.target.value.split('-');
                            try { await api.put(`/invoices/${invoiceId}`, { period_start_date: new Date(parseInt(y), parseInt(m)-1, parseInt(d), 12).toISOString() }); invalidateInvoice(); } catch { /* ignore */ }
                          }
                        }} />
                        <span className="text-xs text-muted-foreground self-center">to</span>
                        <Input type="date" className="h-7 w-36 text-xs date-filled" defaultValue={invoice.period_end_date ? new Date(invoice.period_end_date).toISOString().split('T')[0] : ''} onChange={async (e) => {
                          if (e.target.value) {
                            const [y, m, d] = e.target.value.split('-');
                            try { await api.put(`/invoices/${invoiceId}`, { period_end_date: new Date(parseInt(y), parseInt(m)-1, parseInt(d), 12).toISOString() }); invalidateInvoice(); } catch { /* ignore */ }
                          }
                        }} />
                      </div>
                    )}
                  </div>
                )}
                <div>
                  <div className="flex items-center gap-1 justify-end">
                    <span className="text-muted-foreground">Invoice Date: </span>
                    <span className="font-medium">{invoice.invoice_date && new Date(invoice.invoice_date).toLocaleDateString()}</span>
                    {isDraft && (
                      <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-muted-foreground hover:text-primary" onClick={() => setShowInvDatePicker(!showInvDatePicker)}>
                        <Calendar className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                  {showInvDatePicker && (
                    <div className="mt-1 flex justify-end">
                      <Input type="date" className="h-7 w-36 text-xs date-filled" defaultValue={invoice.invoice_date ? new Date(invoice.invoice_date).toISOString().split('T')[0] : ''} onChange={async (e) => {
                        if (e.target.value) {
                          const [y, m, d] = e.target.value.split('-');
                          const localDate = new Date(parseInt(y), parseInt(m) - 1, parseInt(d), 12, 0, 0);
                          try { await api.put(`/invoices/${invoiceId}`, { invoice_date: localDate.toISOString() }); setShowInvDatePicker(false); invalidateInvoice(); } catch { /* ignore */ }
                        }
                      }} />
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-1 justify-end">
                    <span className="text-muted-foreground">Payment Due: </span>
                    <span className="font-medium">{invoice.invoice_due_date && new Date(invoice.invoice_due_date).toLocaleDateString()}</span>
                    {isDraft && (
                      <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-muted-foreground hover:text-primary" onClick={() => setShowDueDatePicker(!showDueDatePicker)}>
                        <Calendar className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                  {showDueDatePicker && (
                    <div className="mt-1 flex justify-end">
                      <Input type="date" className="h-7 w-36 text-xs date-filled" defaultValue={invoice.invoice_due_date ? new Date(invoice.invoice_due_date).toISOString().split('T')[0] : ''} onChange={async (e) => {
                        if (e.target.value) {
                          const [y, m, d] = e.target.value.split('-');
                          const localDate = new Date(parseInt(y), parseInt(m) - 1, parseInt(d), 12, 0, 0);
                          try { await api.put(`/invoices/${invoiceId}`, { invoice_due_date: localDate.toISOString() }); setShowDueDatePicker(false); invalidateInvoice(); } catch { /* ignore */ }
                        }
                      }} />
                    </div>
                  )}
                </div>
                <div><span className="text-muted-foreground">{amountLabelShort}: </span><span className="font-semibold text-base">{fmt(Math.abs(amountDue))}</span></div>
              </div>
            </div>

            {/* 3. SYNDICATE + HORSE — shared shaded block */}
            {(invoice.syndicate_name || invoice.horse_name) ? (
              <div className="bg-primary/10 rounded px-4 py-2 space-y-0.5">
                {invoice.syndicate_name && (
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Syndicate: </span>
                    <span className="text-sm font-semibold">{invoice.syndicate_name}</span>
                  </div>
                )}
                {invoice.horse_name && (
                  <div>
                    <span className="text-sm font-medium text-muted-foreground">Horse: </span>
                    <span className="text-sm font-semibold">{invoice.horse_name}</span>
                  </div>
                )}
              </div>
            ) : isDraft && (
              <Button variant="outline" size="sm" className="text-xs" onClick={() => setAddHorseOpen(true)}>
                <Plus className="w-3 h-3 mr-1" /> Add Horse
              </Button>
            )}

            {/* 4A. EXPENSES */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-2">
                {hasOtherProceeds ? 'EXPENSES & OTHER PROCEEDS' : 'EXPENSES'}
              </p>
              {(() => {
                // All non-sale entries (both expenses and proceeds) in one unified table
                const allLineEntries = entries.filter(e => !e.sale_opportunity_id);
                if (allLineEntries.length === 0) {
                  return <p className="text-sm text-muted-foreground italic py-4 text-center">No expenses on this invoice</p>;
                }
                return (
                  <table className="w-full text-sm">
                    <thead><tr className="border-b text-xs text-muted-foreground"><th className="text-left py-1">Item</th><th className="text-left py-1">Description</th><th className="text-right py-1">Units</th><th className="text-right py-1">Price</th><th className="text-right py-1">Total</th></tr></thead>
                    <tbody>
                      {allLineEntries.map((e) => {
                        const idx = entries.indexOf(e);
                        const isProceeds = !!e.is_proceeds;
                        const isDirectAlloc = !!e.is_direct_allocation;
                        return (
                          <tr key={idx} className={`border-b border-border/30 ${isDraft ? 'cursor-pointer hover:bg-muted/30' : ''}`} onClick={() => isDraft && startEditEntry(idx)}>
                            <td className="py-2 font-medium">
                              {e.items}
                              {isProceeds && (
                                <span className="ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded bg-muted text-muted-foreground border">Proceeds</span>
                              )}
                              {isDirectAlloc && (
                                <span className="ml-2 text-[10px] font-medium px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200">Direct Allocation</span>
                              )}
                            </td>
                            <td className="py-2 text-muted-foreground text-xs">{e.description || ''}</td>
                            <td className="py-2 text-right">{e.units}</td>
                            <td className="py-2 text-right">{fmt(e.price)}</td>
                            <td className="py-2 text-right font-medium">
                              {isProceeds
                                ? `(${fmt(e.total || e.price * e.units)})`
                                : fmt(e.total || e.price * e.units)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                );
              })()}
              {isDraft && (
                <div className="flex gap-2 mt-2">
                  <Button variant="outline" size="sm" className="text-xs" onClick={() => setAddItemOpen(true)}><Plus className="w-3 h-3 mr-1" /> Add Line Item</Button>
                  {!invoice.add_sale_proceeds && (
                    <Button variant="outline" size="sm" className="text-xs" onClick={() => setAddSaleOpen(true)}><DollarSign className="w-3 h-3 mr-1" /> Add Sale</Button>
                  )}
                </div>
              )}
              <div className="border-t mt-2 pt-2 space-y-1 text-sm">
                {hasDirectAllocations ? (
                  /* ── DIRECT ALLOCATION TOTALS PATH ──────────────────────────── */
                  (() => {
                    const hasSharedProceeds = sharedProceedsSubtotal > 0;
                    // Shared subtotal: proceeds - expenses (positive = payable)
                    const sharedNet = hasSharedProceeds
                      ? sharedProceedsSubtotal - sharedExpenseSubtotal
                      : sharedExpenseSubtotal;
                    const sharedNetPayable = hasSharedProceeds && sharedNet > 0;
                    const sharedDisplay = Math.abs(sharedNet);
                    const discountLabel = invoice.discount_description
                      ? `${invoice.discount_description}${invoice.discount_unit === 'percent' ? ` (${invoice.discount_amount}%)` : ''}`
                      : `Discount${invoice.discount_unit === 'percent' ? ` (${invoice.discount_amount}%)` : ''}`;
                    // Total = proceeds − all expenses (after discount), shown as net
                    const hasAnyProceeds = sharedProceedsSubtotal > 0 || directProceedsSubtotal > 0;
                    const totalAllProceeds  = sharedProceedsSubtotal + directProceedsSubtotal;
                    const totalAllExpenses  = (sharedExpenseSubtotal - discountOnShared) + (directExpenseSubtotal - discountOnDirect);
                    const totalNet          = hasAnyProceeds ? totalAllProceeds - totalAllExpenses : totalAllExpenses;
                    const totalNetPayable   = hasAnyProceeds && totalNet > 0;
                    return (
                      <>
                        <div className="flex justify-end gap-8 text-muted-foreground">
                          <span>Subtotal – Shared Expenses{hasSharedProceeds ? ' & Proceeds' : ''}</span>
                          <span className="w-28 text-right">
                            {sharedNetPayable ? `(${fmt(sharedDisplay)})` : fmt(sharedDisplay)}
                          </span>
                        </div>
                        {/* Direct Allocations — show expenses and proceeds separately when both exist */}
                        {directExpenseSubtotal > 0 && directProceedsSubtotal > 0 ? (
                          <>
                            <div className="flex justify-end gap-8 text-muted-foreground">
                              <span>Subtotal – Direct Allocations (Expenses)</span>
                              <span className="w-28 text-right">{fmt(directExpenseSubtotal)}</span>
                            </div>
                            <div className="flex justify-end gap-8 text-muted-foreground">
                              <span>Subtotal – Direct Allocations (Proceeds)</span>
                              <span className="w-28 text-right">({fmt(directProceedsSubtotal)})</span>
                            </div>
                          </>
                        ) : (
                          <div className="flex justify-end gap-8 text-muted-foreground">
                            <span>Subtotal – Direct Allocations</span>
                            <span className="w-28 text-right">
                              {directProceedsSubtotal > 0
                                ? `(${fmt(directProceedsSubtotal)})`
                                : fmt(directExpenseSubtotal)}
                            </span>
                          </div>
                        )}
                        {invoice.add_discount && invoice.discount_total > 0 && (
                          <div className="flex justify-end gap-8 items-center">
                            <span className="text-right">{discountLabel}</span>
                            <div className="flex items-center gap-1 w-28 justify-end">
                              <span>({fmt(invoice.discount_total)})</span>
                              {isDraft && (
                                <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-muted-foreground hover:text-primary" onClick={() => setEditingDiscount(!editingDiscount)}>
                                  <Pencil className="w-3 h-3" />
                                </Button>
                              )}
                            </div>
                          </div>
                        )}
                        {isDraft && !(invoice.add_discount && invoice.discount_total > 0) && !editingDiscount && (
                          <div className="flex justify-end">
                            <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => {
                              setEditingDiscount(true);
                              api.put(`/invoices/${invoiceId}`, { add_discount: true, discount_unit: 'currency', discount_amount: 0 }).then(() => invalidateInvoice()).catch(() => {});
                            }}>+ Add Discount</Button>
                          </div>
                        )}
                        {isDraft && editingDiscount && (
                          <div className="space-y-1.5">
                            <div className="flex justify-end gap-2 items-center">
                              <Input placeholder="Description (optional)" className="h-7 text-xs w-48"
                                defaultValue={invoice.discount_description || ''}
                                onBlur={async (e) => { try { await api.put(`/invoices/${invoiceId}`, { discount_description: e.target.value }); invalidateInvoice(); } catch { /* ignore */ } }} />
                              <Select value={invoice.discount_unit || 'currency'} onValueChange={async (v) => { try { await api.put(`/invoices/${invoiceId}`, { discount_unit: v }); invalidateInvoice(); } catch { /* ignore */ } }}>
                                <SelectTrigger className="h-7 w-20 text-xs"><SelectValue /></SelectTrigger>
                                <SelectContent><SelectItem value="currency">$</SelectItem><SelectItem value="percent">%</SelectItem></SelectContent>
                              </Select>
                              <Input type="number" className="h-7 w-24 text-xs" defaultValue={invoice.discount_amount || 0} onBlur={async (e) => {
                                const amt = parseFloat(e.target.value) || 0;
                                const expTotal = entries.filter(en => !en.sale_opportunity_id).reduce((s, en) => s + (en.total || en.price * en.units), 0);
                                const discTotal = invoice.discount_unit === 'percent' ? expTotal * (amt / 100) : amt;
                                const invTotal = expTotal - discTotal;
                                const ownerTotal = (invoice.owner_percent / 100) * invTotal;
                                try { await api.put(`/invoices/${invoiceId}`, { discount_amount: amt, discount_total: discTotal, invoice_total: invTotal, owner_portion_total: ownerTotal }); setEditingDiscount(false); invalidateInvoice(); } catch { /* ignore */ }
                              }} />
                              <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={async () => { try { await api.put(`/invoices/${invoiceId}`, { add_discount: false, discount_amount: 0, discount_total: 0, discount_description: '' }); setEditingDiscount(false); invalidateInvoice(); } catch { /* ignore */ } }}>Remove</Button>
                            </div>
                          </div>
                        )}
                        <div className="flex justify-end gap-8 font-semibold">
                          <span>{hasAnyProceeds ? 'Total Expenses & Proceeds' : 'Total Expenses'}</span>
                          <span className="w-28 text-right">
                            {totalNetPayable ? `(${fmt(totalNet)})` : fmt(Math.abs(totalNet))}
                          </span>
                        </div>
                      </>
                    );
                  })()
                ) : (
                  /* ── STANDARD TOTALS PATH (identical to original) ──────────── */
                  <>
                    {hasOtherProceeds && (
                      <>
                        <div className="flex justify-end gap-8 text-muted-foreground">
                          <span>Expenses</span>
                          <span className="w-28 text-right">{fmt(expenseSubtotal)}</span>
                        </div>
                        <div className="flex justify-end gap-8 text-muted-foreground">
                          <span>Other Proceeds</span>
                          <span className="w-28 text-right">({fmt(proceedsSubtotal)})</span>
                        </div>
                      </>
                    )}
                    {(() => {
                      const expSectionNet = hasOtherProceeds ? proceedsSubtotal - expenseSubtotal : expenseSubtotal;
                      const expSectionPayable = hasOtherProceeds && expSectionNet > 0;
                      return (
                        <div className="flex justify-end gap-8">
                          <span className="text-muted-foreground">Subtotal</span>
                          <span className="font-medium w-28 text-right">
                            {expSectionPayable ? `(${fmt(expSectionNet)})` : fmt(Math.abs(expSectionNet))}
                          </span>
                        </div>
                      );
                    })()}
                    {invoice.add_discount && invoice.discount_total > 0 && (
                      <div className="flex justify-end gap-8 items-center">
                        <span className="text-right">
                          {invoice.discount_description
                            ? `${invoice.discount_description}${invoice.discount_unit === 'percent' ? ` (${invoice.discount_amount}%)` : ''}`
                            : `Discount${invoice.discount_unit === 'percent' ? ` (${invoice.discount_amount}%)` : ''}`}
                        </span>
                        <div className="flex items-center gap-1 w-28 justify-end">
                          <span>({fmt(invoice.discount_total)})</span>
                          {isDraft && (
                            <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-muted-foreground hover:text-primary" onClick={() => setEditingDiscount(!editingDiscount)}>
                              <Pencil className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </div>
                    )}
                    {isDraft && !(invoice.add_discount && invoice.discount_total > 0) && !editingDiscount && (
                      <div className="flex justify-end">
                        <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={() => {
                          setEditingDiscount(true);
                          api.put(`/invoices/${invoiceId}`, { add_discount: true, discount_unit: 'currency', discount_amount: 0 }).then(() => invalidateInvoice()).catch(() => {});
                        }}>+ Add Discount</Button>
                      </div>
                    )}
                    {isDraft && editingDiscount && (
                      <div className="space-y-1.5">
                        <div className="flex justify-end gap-2 items-center">
                          <Input placeholder="Description (optional)" className="h-7 text-xs w-48"
                            defaultValue={invoice.discount_description || ''}
                            onBlur={async (e) => {
                              try { await api.put(`/invoices/${invoiceId}`, { discount_description: e.target.value }); invalidateInvoice(); } catch { /* ignore */ }
                            }} />
                          <Select value={invoice.discount_unit || 'currency'} onValueChange={async (v) => {
                            try { await api.put(`/invoices/${invoiceId}`, { discount_unit: v }); invalidateInvoice(); } catch { /* ignore */ }
                          }}>
                            <SelectTrigger className="h-7 w-20 text-xs"><SelectValue /></SelectTrigger>
                            <SelectContent><SelectItem value="currency">$</SelectItem><SelectItem value="percent">%</SelectItem></SelectContent>
                          </Select>
                          <Input type="number" className="h-7 w-24 text-xs" defaultValue={invoice.discount_amount || 0} onChange={(e) => e.target.dataset.val = e.target.value} onBlur={async (e) => {
                            const amt = parseFloat(e.target.value) || 0;
                            const expTotal = entries.filter(en => !en.sale_opportunity_id).reduce((s, en) => s + (en.total || en.price * en.units), 0);
                            const discTotal = invoice.discount_unit === 'percent' ? expTotal * (amt / 100) : amt;
                            const invTotal = expTotal - discTotal;
                            const ownerTotal = (invoice.owner_percent / 100) * invTotal;
                            try { await api.put(`/invoices/${invoiceId}`, { discount_amount: amt, discount_total: discTotal, invoice_total: invTotal, owner_portion_total: ownerTotal }); setEditingDiscount(false); invalidateInvoice(); } catch { /* ignore */ }
                          }} />
                          <Button variant="ghost" size="sm" className="h-7 text-xs text-muted-foreground" onClick={async () => {
                            try { await api.put(`/invoices/${invoiceId}`, { add_discount: false, discount_amount: 0, discount_total: 0, discount_description: '' }); setEditingDiscount(false); invalidateInvoice(); } catch { /* ignore */ }
                          }}>Remove</Button>
                        </div>
                      </div>
                    )}
                    {(() => {
                      const expSectionNet = hasOtherProceeds ? proceedsSubtotal - expenseSubtotal : expenseTotal;
                      const expSectionPayable = hasOtherProceeds && expSectionNet > 0;
                      return (
                        <div className="flex justify-end gap-8 font-semibold">
                          <span>{hasOtherProceeds ? 'Total Expenses & Other Proceeds' : 'Total Expenses'}</span>
                          <span className="w-28 text-right">
                            {expSectionPayable ? `(${fmt(expSectionNet)})` : fmt(hasOtherProceeds ? Math.abs(expSectionNet) : expenseTotal)}
                          </span>
                        </div>
                      );
                    })()}
                  </>
                )}
              </div>
            </div>

            {/* 4B. SALE DETAILS */}
            {invoice.add_sale_proceeds && (
              <div className="border rounded py-4 -mx-0">
                <div className="flex items-center justify-between mb-2 px-4">
                  <p className="text-xs font-semibold text-muted-foreground">SALE DETAILS</p>
                  {isDraft && (
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-muted-foreground hover:text-primary" onClick={() => navigate(`/sales/opportunities/${invoice.sale_opportunity_id}?tab=financials`)} title="View Sale Opportunity">
                        <Pencil className="w-3 h-3" />
                      </Button>
                      <Button variant="ghost" size="sm" className="h-5 w-5 p-0 text-muted-foreground hover:text-red-500" title="Remove Sale Details" onClick={() => {
                        setConfirmAction({
                          message: 'Remove sale details from this invoice?',
                          onConfirm: async () => {
                            try {
                              await api.put(`/invoices/${invoiceId}`, { add_sale_proceeds: false, sale_net_proceeds: 0, sale_opportunity_id: null, owner_portion_sale_net_proceeds: 0, commission_amount: 0 });
                              toast.success('Sale details removed');
                              invalidateInvoice();
                            } catch { toast.error('Failed'); }
                            setConfirmAction(null);
                          }
                        });
                      }}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  )}
                </div>
                <div className="bg-primary/10 rounded px-4 py-2 mb-3 flex gap-6 text-sm justify-center">
                  <div><span className="text-muted-foreground">Sale: </span><span className="font-medium">{invoice.sale_full_name || '-'}</span></div>
                  <div><span className="text-muted-foreground">Hip #: </span><span className="font-medium">#{invoice.sale_hip_number || 'TBD'}</span></div>
                  {invoice.signed_ticket_as && <div><span className="text-muted-foreground">Buyer: </span><span className="font-medium">{invoice.signed_ticket_as}</span></div>}
                  {invoice.sale_result === 'rna' && (
                    <div><span className="bg-yellow-100 text-yellow-800 text-xs font-semibold px-2 py-0.5 rounded">RNA</span></div>
                  )}
                </div>
                <div className="space-y-1 text-sm px-4">
                  {invoice.sale_result !== 'rna' && (
                    <>
                      <div className="flex justify-between"><span>Sale Price</span><span className="font-medium text-right w-32">{fmt(invoice.sale_price)}</span></div>
                      {invoice.sale_price_notes && <p className="text-xs text-muted-foreground italic">Note: {invoice.sale_price_notes}</p>}
                    </>
                  )}
                  {/* Fee rows — positive for RNA, negative for sold */}
                  {[
                    [(invoice.sale_company_chargeback || 0), 'Charge Back'],
                    [(invoice.sale_company_entry_fee || 0), 'Entry Fee'],
                    [(invoice.sale_company_commission || 0), 'Sale Commission'],
                    [(invoice.sale_company_lien || 0), 'Liens'],
                    [(invoice.sale_company_other_amount || 0), 'Other Fees'],
                  ].filter(([v]) => v > 0).map(([v, label]) => (
                    <div key={label} className="flex justify-between">
                      <span>{label}</span>
                      <span className="text-right w-32">
                        {invoice.sale_result === 'rna' ? fmt(v) : `-${fmt(v)}`}
                      </span>
                    </div>
                  ))}
                  {invoice.sale_company_other_notes && <p className="text-xs text-muted-foreground italic">Note: {invoice.sale_company_other_notes}</p>}
                  {invoice.sale_result === 'rna' ? (
                    <>
                      <div className="border-t pt-1 flex justify-between font-semibold">
                        <span>Total Sale Company Fees</span>
                        <span className="text-right w-32">{fmt(saleFees)}</span>
                      </div>
                      {invoice.commission_amount > 0 && (
                        <div className="flex justify-between">
                          <span>Minimum Commission</span>
                          <span className="text-right w-32">{fmt(invoice.commission_amount)}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-semibold text-primary">
                        <span>Total Sale Costs</span>
                        <span className="text-right w-32">{fmt(saleFees + (invoice.commission_amount || 0))}</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="border-t pt-1 flex justify-between font-semibold"><span>Sale Net Proceeds</span><span className="text-right w-32">{fmt(saleNetProceeds)}</span></div>
                      {invoice.commission_amount > 0 && (
                        <div className="flex justify-between">
                          <span>
                            {(() => {
                              // Use gross sale price as base — commission is on gross, not net
                              const grossBase = invoice.sale_price || invoice.sale_net_proceeds || 0;
                              const calcByRate = (grossBase * (invoice.commission_rate || 0)) / 100;
                              const isMinimum = invoice.minimum_commission_amount > 0 &&
                                Math.abs(invoice.commission_amount - invoice.minimum_commission_amount) < 0.01;
                              if (isMinimum) return 'Minimum Commission';
                              return invoice.commission_split_applied
                                ? `Commission (${100 - invoice.commission_split_percent}% of ${invoice.commission_rate}% Commission)`
                                : `Commission (${invoice.commission_rate}%)`;
                            })()}
                          </span>
                          <span className="text-right w-32">({fmt(invoice.commission_amount)})</span>
                        </div>
                      )}
                      <div className="flex justify-between font-semibold text-primary"><span>Owner Sale Proceeds</span><span className="text-right w-32">{fmt(fullSaleProceeds)}</span></div>
                    </>
                  )}
                </div>
              </div>
            )}

            {/* 4C. TOTAL + OWNERSHIP + AMOUNT DUE */}
            <div className="border-t border-b py-3 space-y-1 text-sm">
              <div className="flex justify-end gap-8 font-semibold text-base">
                <span>{hasDirectAllocations ? 'Shared Total' : 'Total'}</span>
                <span className="w-28 text-right">{fmt(Math.abs(hasDirectAllocations ? sharedGrandTotal : grandTotal))}</span>
              </div>
              {invoice.owner_percent != null && invoice.owner_percent < 100 && (
                <div className="flex justify-end"><span className="w-28 text-right">Ownership: {invoice.owner_percent}%</span></div>
              )}
              {hasDirectAllocations && (
                <>
                  <div className="flex justify-end gap-8 text-muted-foreground">
                    <span>Owner Portion of Shared Total</span>
                    <span className="w-28 text-right">{fmt(sharedGrandTotal * ((invoice.owner_percent ?? 100) / 100))}</span>
                  </div>
                  <div className="flex justify-end gap-8 text-muted-foreground">
                    <span>Direct Allocation Total</span>
                    <span className="w-28 text-right">
                      {directSubtotal < 0
                        ? `(${fmt(Math.abs(directSubtotal))})`  // net proceeds → parentheses
                        : fmt(directSubtotal)}
                    </span>
                  </div>
                </>
              )}
              <div className="flex justify-end gap-8 font-bold text-base"><span>{amountLabel}</span><span className="w-28 text-right">{fmt(Math.abs(amountDue))}</span></div>
            </div>

            {/* 5. NOTES / ADDITIONAL TERMS */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-1">
                <p className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">Notes / Additional Terms</p>
                {isDraft && !notesTermsEditing && (
                  <Button
                    variant="ghost" size="icon" className="h-6 w-6 text-muted-foreground hover:text-foreground"
                    title="Edit notes / additional terms"
                    data-testid="edit-notes-terms-btn"
                    onClick={() => { setNotesTermsEdit(invoice.other_notes_terms || ''); setNotesTermsEditing(true); }}
                  >
                    <Pencil className="w-3 h-3" />
                  </Button>
                )}
              </div>
              {notesTermsEditing ? (
                <div className="space-y-2">
                  <Textarea
                    value={notesTermsEdit}
                    onChange={e => setNotesTermsEdit(e.target.value)}
                    placeholder="e.g. Payment due within 30 days. Thank you for your business."
                    rows={3}
                    className="text-sm"
                    autoFocus
                    data-testid="notes-terms-textarea"
                  />
                  <div className="flex gap-2">
                    <Button size="sm" className="bg-primary hover:bg-primary/90 text-white"
                      onClick={handleSaveNotesTerms} disabled={savingNotesTerms}
                      data-testid="save-notes-terms-btn">
                      {savingNotesTerms ? 'Saving…' : 'Save'}
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => setNotesTermsEditing(false)}
                      data-testid="cancel-notes-terms-btn">
                      Cancel
                    </Button>
                  </div>
                </div>
              ) : invoice.other_notes_terms ? (
                <p className="text-sm whitespace-pre-wrap" data-testid="notes-terms-display">
                  {invoice.other_notes_terms}
                </p>
              ) : (
                <p className="text-xs text-muted-foreground italic" data-testid="notes-terms-empty">
                  None — click the pencil to add notes or additional terms that will appear on this invoice.
                </p>
              )}
            </div>

            {/* ATTACHMENTS */}
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-2">ATTACHMENTS</p>
              {(invoice.attachments || []).length > 0 && (
                <div className="space-y-1 mb-2">
                  {(invoice.attachments || []).map((att, i) => (
                    <div key={i} className="flex items-center gap-2 p-2 border rounded text-sm">
                      <Paperclip className="w-3 h-3 text-muted-foreground" />
                      <span className="flex-1 text-primary hover:underline cursor-pointer" onClick={() => setPreviewAttachment(att)}>{att.file_name}</span>
                      {isDraft && (
                        <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-muted-foreground hover:text-red-500" onClick={async () => {
                          const updated = (invoice.attachments || []).filter((_, idx) => idx !== i);
                          try { await api.put(`/invoices/${invoiceId}`, { attachments: updated }); invalidateInvoice(); } catch { /* ignore */ }
                        }}><Trash2 className="w-3 h-3" /></Button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              {(invoice.attachments || []).length === 0 && !isDraft && (
                <p className="text-xs text-muted-foreground italic mb-2">No attachments</p>
              )}
              {isDraft && (
                <div className="mt-1">
                  <div
                    className="border-2 border-dashed rounded p-4 text-center cursor-pointer hover:bg-muted/30 transition-colors"
                    onClick={() => document.getElementById('invoice-attachment')?.click()}
                    onDragOver={(e) => { e.preventDefault(); e.currentTarget.classList.add('bg-muted/50'); }}
                    onDragLeave={(e) => { e.preventDefault(); e.currentTarget.classList.remove('bg-muted/50'); }}
                    onDrop={async (e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove('bg-muted/50');
                      const file = e.dataTransfer.files?.[0];
                      if (file) {
                        const formData = new FormData();
                        formData.append('file', file);
                        try {
                          const res = await api.post(`/invoices/${invoiceId}/upload-attachment`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                          const updated = [...(invoice.attachments || []), { file_name: res.data.file_name, file_id: res.data.file_id, file_url: res.data.file_url }];
                          await api.put(`/invoices/${invoiceId}`, { attachments: updated });
                          toast.success('Attachment added');
                          invalidateInvoice();
                        } catch { toast.error('Upload failed'); }
                      }
                    }}
                  >
                    <Paperclip className="w-5 h-5 mx-auto mb-1 text-muted-foreground" />
                    <p className="text-xs text-muted-foreground">Drag & drop or click to attach a file</p>
                  </div>
                  <input id="invoice-attachment" type="file" className="hidden" onChange={async (e) => {
                    const file = e.target.files?.[0];
                    if (!file) return;
                    const formData = new FormData();
                    formData.append('file', file);
                    try {
                      const res = await api.post(`/invoices/${invoiceId}/upload-attachment`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
                      const updated = [...(invoice.attachments || []), { file_name: res.data.file_name, file_id: res.data.file_id, file_url: res.data.file_url }];
                      await api.put(`/invoices/${invoiceId}`, { attachments: updated });
                      toast.success('Attachment added');
                      invalidateInvoice();
                    } catch { toast.error('Upload failed'); }
                    e.target.value = '';
                  }} />
                </div>
              )}
            </div>

            {/* 6. FOOTER */}
            {invoice.tenant_invoice_footer && (
              <div className="text-center text-xs text-muted-foreground border-t pt-4">
                <p>{invoice.tenant_invoice_footer}</p>
              </div>
            )}
            {/* 6. POWERED BY */}
            <div className="text-center pt-4">
              <img src={GREEN_DOOR_LOGO} alt="Powered by Green Door" className="w-1/4 mx-auto opacity-60" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Add Line Item Dialog */}
      <AddLineItemDialog open={addItemOpen} onOpenChange={setAddItemOpen} standardItems={standardItems} onAdd={addLineItem} ownerPercent={invoice.owner_percent ?? 100} />

      {/* Add Sale Dialog */}
      <Dialog open={addSaleOpen} onOpenChange={setAddSaleOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Add Sale to Invoice</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">Select a sale opportunity to add to this invoice.</p>
            {saleOpportunities.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">
                {invoice?.horse_id ? 'There are no sale opportunities for this Horse.' : 'No sale opportunities available.'}
              </p>
            ) : (
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {saleOpportunities.map(opp => {
                  const resultBadge = (() => {
                    switch (opp.result) {
                      case 'sold':       return { label: 'Sold',      cls: 'bg-green-100 text-green-700' };
                      case 'rna':        return { label: 'RNA',       cls: 'bg-yellow-100 text-yellow-700' };
                      case 'withdrawn':  return { label: 'Withdrawn', cls: 'bg-red-100 text-red-700' };
                      case 'pending':    return { label: 'Pending',   cls: 'bg-gray-100 text-gray-600' };
                      default:           return { label: opp.result ? opp.result.charAt(0).toUpperCase() + opp.result.slice(1) : '–', cls: 'bg-gray-100 text-gray-500' };
                    }
                  })();
                  return (
                    <div key={opp.id} className="flex items-center justify-between p-3 border rounded hover:bg-muted/50 cursor-pointer" onClick={() => handleAddSale(opp.id)}>
                      <div>
                        <p className="text-sm font-medium">{opp.opportunity_name?.split(' - ')[1] || opp.opportunity_name}</p>
                        <p className="text-xs text-muted-foreground">{opp.sale_year} {opp.sale_company} - {opp.sale_name} · Hip #{opp.hip_number || 'TBD'}</p>
                      </div>
                      <Badge className={`text-xs ${resultBadge.cls}`}>{resultBadge.label}</Badge>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddSaleOpen(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Commission Split Dialog */}

      {/* Add Horse Dialog */}
      <Dialog open={addHorseOpen} onOpenChange={setAddHorseOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Add Horse to Invoice</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <Select onValueChange={async (v) => {
              try {
                await api.put(`/invoices/${invoiceId}`, { horse_id: v });
                toast.success('Horse added');
                setAddHorseOpen(false);
                invalidateInvoice();
              } catch { toast.error('Failed'); }
            }}>
              <SelectTrigger><SelectValue placeholder="Select horse..." /></SelectTrigger>
              <SelectContent>
                {horses.map(h => <SelectItem key={h.id} value={h.id}>{h.system_name || h.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddHorseOpen(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Commission Split Dialog */}
      {commissionSplitDialog && (
        <Dialog open={!!commissionSplitDialog} onOpenChange={(v) => { if (!v) setCommissionSplitDialog(null); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader><DialogTitle>Commission Split</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <p className="text-sm">
                This client has a <strong>{commissionSplitDialog.commissionSplitPercent}%</strong> commission split
                {commissionSplitDialog.splitEntityName && ` with ${commissionSplitDialog.splitEntityName}`}.
              </p>
              <p className="text-sm text-muted-foreground">
                How should the {commissionSplitDialog.commissionRate}% commission be reflected on this invoice?
              </p>
              <div className="space-y-2">
                <Button variant="outline" className="w-full justify-start text-left h-auto py-3" onClick={() => handleCommissionSplitChoice(true)}>
                  <div>
                    <p className="font-medium">My Portion Only</p>
                    <p className="text-xs text-muted-foreground">Show {100 - commissionSplitDialog.commissionSplitPercent}% of {commissionSplitDialog.commissionRate}% commission on this invoice</p>
                  </div>
                </Button>
                <Button variant="outline" className="w-full justify-start text-left h-auto py-3" onClick={() => handleCommissionSplitChoice(false)}>
                  <div>
                    <p className="font-medium">Full Commission</p>
                    <p className="text-xs text-muted-foreground">Show full {commissionSplitDialog.commissionRate}% commission and create a separate invoice for the {commissionSplitDialog.commissionSplitPercent}% split payment</p>
                  </div>
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCommissionSplitDialog(null)}>Cancel</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Line Item Dialog */}
      <Dialog open={editEntryIdx !== null} onOpenChange={(v) => { if (!v) setEditEntryIdx(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader><DialogTitle>Edit Line Item</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2"><Label>Item</Label><Input value={editEntryForm.items} onChange={(e) => setEditEntryForm(f => ({ ...f, items: e.target.value }))} /></div>
            <div className="space-y-2"><Label>Description</Label><Input value={editEntryForm.description} onChange={(e) => setEditEntryForm(f => ({ ...f, description: e.target.value }))} /></div>
            <div className="space-y-2">
              <Label>Units</Label>
              <Input type="number" value={editEntryForm.units} onChange={(e) => setEditEntryForm(f => ({ ...f, units: parseFloat(e.target.value) || 1 }))} />
            </div>
            {editEntryForm.is_rebillable ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2"><Label>Actual Cost</Label><Input type="number" value={editEntryForm.base_cost} onChange={(e) => setEditEntryForm(f => ({ ...f, base_cost: parseFloat(e.target.value) || 0 }))} /></div>
                  <div className="space-y-2"><Label>Markup %</Label><Input type="number" value={editEntryForm.markup_percent} onChange={(e) => setEditEntryForm(f => ({ ...f, markup_percent: parseFloat(e.target.value) || 0 }))} /></div>
                </div>
                <div className="text-sm text-muted-foreground">
                  Billed Price: {fmt(editEntryForm.base_cost * (1 + editEntryForm.markup_percent / 100))}
                </div>
              </>
            ) : (
              <div className="space-y-2"><Label>Price</Label><Input type="number" value={editEntryForm.price} onChange={(e) => setEditEntryForm(f => ({ ...f, price: parseFloat(e.target.value) || 0 }))} /></div>
            )}
            <div className="flex flex-col gap-2">
              {/* Only show Rebillable checkbox for expense entries, not for proceeds entries */}
              {editEntryIdx !== null && !entries[editEntryIdx]?.is_proceeds && (
                <div className="flex items-center gap-2">
                  <Checkbox id="edit-rebillable" checked={!!editEntryForm.is_rebillable} onCheckedChange={(v) => setEditEntryForm(f => ({ ...f, is_rebillable: v }))} />
                  <Label htmlFor="edit-rebillable" className="text-sm">Rebillable Expense</Label>
                </div>
              )}
              {/* Direct Allocation — only for draft invoices on horses with multiple owners */}
              {isDraft && (invoice.owner_percent ?? 100) < 100 && (
                <div className="flex items-center gap-2">
                  <Checkbox id="edit-direct-alloc" checked={!!editEntryForm.is_direct_allocation} onCheckedChange={(v) => setEditEntryForm(f => ({ ...f, is_direct_allocation: v }))} />
                  <Label htmlFor="edit-direct-alloc" className="text-sm">Direct Allocation <span className="text-xs text-muted-foreground">(applies solely to this Account)</span></Label>
                </div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="destructive" size="sm" onClick={() => { deleteEntry(editEntryIdx); setEditEntryIdx(null); }} className="mr-auto"><Trash2 className="w-4 h-4" /></Button>
            <Button variant="outline" onClick={() => setEditEntryIdx(null)}>Cancel</Button>
            <Button onClick={saveEditEntry} className="bg-primary hover:bg-primary/90 text-white">Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Attachment Preview */}
      {previewAttachment && (
        <Dialog open={!!previewAttachment} onOpenChange={(v) => { if (!v) setPreviewAttachment(null); }}>
          <DialogContent className="sm:max-w-5xl max-h-[90vh]">
            <DialogHeader>
              <DialogTitle>{previewAttachment.file_name || 'Attachment Preview'}</DialogTitle>
            </DialogHeader>
            <iframe
              src={`${window.location.origin}/api/${(previewAttachment.file_url || `invoices/serve-attachment/${previewAttachment.file_id}${previewAttachment.file_name?.match(/\.[^.]+$/)?.[0] || '.pdf'}`).replace(/^\//, '')}`}
              title="Attachment Preview"
              className="w-full h-[70vh] border-0 rounded"
            />
            <DialogFooter>
              <Button variant="outline" onClick={() => setPreviewAttachment(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Publish to Portal Dialog */}
      <Dialog open={publishDialogOpen} onOpenChange={setPublishDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Publish to Owner Portal</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-3">
            <p className="text-sm">
              This invoice will be published to the <span className="font-semibold">{invoice?.crm_entity_name}</span> Owner Portal.
            </p>
            {is_payable_invoice ? (
              <div className="p-3 bg-green-50 border border-green-200 rounded text-sm">
                <p className="font-medium text-green-800">{amountLabelShort}: {fmt(Math.abs(amountDue))}</p>
                <p className="text-green-700 text-xs mt-1">This invoice will appear in the owner's Document Vault under "All Invoices".</p>
              </div>
            ) : invoice?.payment_status !== 'paid' && invoice?.owner_portion_total > 0 ? (
              <div className="p-3 bg-yellow-50 border border-yellow-200 rounded text-sm">
                <p className="font-medium text-yellow-800">{amountLabelShort}: {fmt(Math.abs(amountDue))}</p>
                <p className="text-yellow-700 text-xs mt-1">This invoice will appear in the owner's "Unpaid Invoices" action items.</p>
              </div>
            ) : (
              <div className="p-3 bg-green-50 border border-green-200 rounded text-sm">
                <p className="text-green-800 text-xs">This invoice will appear in the owner's Document Vault.</p>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPublishDialogOpen(false)}>Cancel</Button>
            <Button
              className="bg-primary hover:bg-primary/90 text-white"
              disabled={publishing}
              onClick={async () => {
                setPublishing(true);
                try {
                  await api.post(`/invoices/${invoice.id}/publish-to-portal`);
                  toast.success('Invoice published to owner portal');
                  setPublishDialogOpen(false);
                  invalidateInvoice();
                } catch { toast.error('Failed to publish'); }
                setPublishing(false);
              }}
            >
              {publishing ? 'Publishing...' : 'Publish'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Invoice Email Dialog */}
      <InvoiceEmailDialog
        open={emailDialogOpen}
        onClose={(sent) => {
          setEmailDialogOpen(false);
          if (sent) invalidateInvoice();
        }}
        invoiceId={invoiceId}
        invoiceNumber={invoice?.invoice_number_formatted}
      />

      {/* Inline Confirmation Dialog */}
      {/* View / Edit Payment Notes Dialog */}
      <Dialog open={payNotesViewOpen} onOpenChange={(v) => { setPayNotesViewOpen(v); setPayNotesEditing(false); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Payment Notes</DialogTitle>
          </DialogHeader>
          <div className="py-2 min-h-[80px]">
            {payNotesEditing ? (
              <Textarea
                value={payNotesEdit}
                onChange={e => setPayNotesEdit(e.target.value)}
                rows={4}
                className="text-sm"
                autoFocus
              />
            ) : (
              <p className="text-sm whitespace-pre-wrap">{invoice?.payment_notes || '—'}</p>
            )}
          </div>
          <div className={`mt-2 flex items-center ${payNotesEditing ? 'justify-end gap-2' : 'justify-start'}`}>
            {/* Pencil bottom-left — hidden while editing */}
            {!payNotesEditing ? (
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-muted-foreground"
                title="Edit notes"
                onClick={() => { setPayNotesEdit(invoice?.payment_notes || ''); setPayNotesEditing(true); }}>
                <Pencil className="w-3.5 h-3.5" />
              </Button>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={() => setPayNotesEditing(false)}>Cancel</Button>
                <Button size="sm" className="bg-primary hover:bg-primary/90 text-white"
                  onClick={() => {
                    api.put(`/invoices/${invoiceId}`, { payment_notes: payNotesEdit })
                      .then(() => { invalidateInvoice(); setPayNotesEditing(false); toast.success('Notes saved'); })
                      .catch(() => toast.error('Failed to save'));
                  }}>
                  Save
                </Button>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Pay Notes Dialog */}
      <Dialog open={payNotesOpen} onOpenChange={setPayNotesOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Mark as Paid</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <Label className="text-sm">Payment Notes (optional)</Label>
            <Textarea
              value={payNotes}
              onChange={e => setPayNotes(e.target.value)}
              placeholder="e.g. Paid via bank transfer on 15 March 2026, ref #12345"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPayNotesOpen(false)}>Cancel</Button>
            <Button className="bg-primary hover:bg-primary/90 text-white" onClick={handlePay}>
              <DollarSign className="w-4 h-4 mr-2" /> Confirm Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmAction} onOpenChange={(open) => { if (!open) setConfirmAction(null); }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground py-2">{confirmAction?.message}</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmAction(null)}>Cancel</Button>
            <Button variant="destructive" onClick={confirmAction?.onConfirm}>Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SidebarLayout>
  );
};

const AddLineItemDialog = ({ open, onOpenChange, standardItems, onAdd, ownerPercent = 100 }) => {
  const [form, setForm] = useState({ items: '', description: '', units: 1, price: 0, is_rebillable: false, is_proceeds: false, is_direct_allocation: false });

  // Reset form state every time the dialog opens (prevents stale DA/rebillable flags)
  React.useEffect(() => {
    if (open) {
      setForm({ items: '', description: '', units: 1, price: 0, is_rebillable: false, is_proceeds: false, is_direct_allocation: false });
    }
  }, [open]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader><DialogTitle>Add Line Item</DialogTitle></DialogHeader>
        <div className="space-y-4 py-2">
          {standardItems.length > 0 && (
            <div className="space-y-2">
              <Label>From Saved Items</Label>
              <Select onValueChange={(v) => { const item = standardItems.find(s => s.id === v); if (item) setForm({ items: item.items, description: item.description || '', units: 1, price: item.price, is_rebillable: item.is_rebillable || false, is_proceeds: item.is_proceeds || false, is_direct_allocation: item.is_direct_allocation || false }); }}>
                <SelectTrigger className="text-sm"><SelectValue placeholder="Select saved item..." /></SelectTrigger>
                <SelectContent>{standardItems.map(s => <SelectItem key={s.id} value={s.id}>{s.items} ({fmt(s.price)}){s.is_rebillable ? ' (R)' : ''}</SelectItem>)}</SelectContent>
              </Select>
              <div className="text-center text-xs text-muted-foreground">— or create custom —</div>
            </div>
          )}
          <div className="space-y-2"><Label>Item *</Label><Input value={form.items} onChange={(e) => setForm(f => ({ ...f, items: e.target.value }))} /></div>
          <div className="space-y-2"><Label>Description</Label><Input value={form.description} onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))} /></div>
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2"><Label>Units</Label><Input type="number" value={form.units} onChange={(e) => setForm(f => ({ ...f, units: parseFloat(e.target.value) || 1 }))} /></div>
            <div className="space-y-2"><Label>Price</Label><Input type="number" value={form.price} onChange={(e) => setForm(f => ({ ...f, price: parseFloat(e.target.value) || 0 }))} /></div>
          </div>

          {/* Entry Type box — contains Rebillable and Direct Allocation */}
          <div className="rounded-lg border bg-muted/20 p-3 space-y-3">
            <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Entry Type</p>
            <div className="flex items-start gap-3">
              <div className="flex items-center gap-2 flex-1 cursor-pointer" onClick={() => setForm(f => ({ ...f, is_proceeds: false, is_rebillable: false }))}>
                <input type="radio" id="type-expense" name="entry-type" checked={!form.is_proceeds} onChange={() => setForm(f => ({ ...f, is_proceeds: false }))} className="w-4 h-4" />
                <div>
                  <Label htmlFor="type-expense" className="text-sm font-medium cursor-pointer">Expense</Label>
                  <p className="text-xs text-muted-foreground">Charged to client (e.g. training, vet fees)</p>
                </div>
              </div>
              <div className="flex items-start gap-2 flex-1 cursor-pointer" onClick={() => setForm(f => ({ ...f, is_proceeds: true, is_rebillable: false }))}>
                <input type="radio" id="type-proceeds" name="entry-type" checked={!!form.is_proceeds} onChange={() => setForm(f => ({ ...f, is_proceeds: true, is_rebillable: false }))} className="w-4 h-4 mt-0.5" />
                <div>
                  <Label htmlFor="type-proceeds" className="text-sm font-medium cursor-pointer">Other Proceeds</Label>
                  <p className="text-xs text-muted-foreground">Paid out to owner (e.g. race winnings, prize money)</p>
                </div>
              </div>
            </div>

            {/* Rebillable — only for Expense entries */}
            {!form.is_proceeds && (
              <div className="flex items-center gap-2 pt-1">
                <Checkbox id="add-rebillable" checked={!!form.is_rebillable} onCheckedChange={(v) => setForm(f => ({ ...f, is_rebillable: v }))} />
                <Label htmlFor="add-rebillable" className="text-sm">Rebillable Expense (markup applies)</Label>
              </div>
            )}

            {/* Direct Allocation — only when horse has multiple owners */}
            {ownerPercent < 100 && (
              <div className="flex items-center gap-2 pt-1">
                <Checkbox id="add-direct-alloc" checked={!!form.is_direct_allocation} onCheckedChange={(v) => setForm(f => ({ ...f, is_direct_allocation: v }))} />
                <div>
                  <Label htmlFor="add-direct-alloc" className="text-sm">Direct Allocation</Label>
                  <p className="text-xs text-muted-foreground">If Multiple Owners, applies solely to this Account</p>
                </div>
              </div>
            )}
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={() => { if (!form.items) { toast.error('Item name required'); return; } onAdd(form); }} className="bg-primary hover:bg-primary/90 text-white">Add Item</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* Editable sale fee line for drafts */
export default InvoiceDetailPage;
